import React, { useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Share2, Link2, ShoppingCart } from 'lucide-react';
import { addToCart, setCartItems } from '../../redux/cartSlice';
import ProductSpecs from './ProductSpecs';
import { AuthContext } from '../../context/AuthContext';
import { apiFetch } from '../../utils/api';
import '../../styles/productDetail.css';

/**
 * ProductInfo panel redesigned for Phase 2 wholesale conversion goals.
 * Renders name, badges, pricing, in-flow cart/Buy Now actions,
 * inline share icons, dynamic specifications, and mobile sticky contact bar.
 *
 * @param {Object} props
 * @param {Object} props.product - The product database document
 * @param {string} props.sectionColor - Accent color dynamically fetched from ViewConfig
 */
const ProductInfo = ({ product, sectionColor }) => {
  const dispatch = useDispatch();
  const { user } = useContext(AuthContext);
  const cartItems = useSelector((state) => state.cart.cartItems) || [];
  const [copied, setCopied] = useState(false);
  
  if (!product) return null;

  const categoryName = product.category?.name || 'General';
  const formattedMoq = product.moqType === 'set'
    ? `MOQ: ${product.moq || 1} Sets${product.unitsPerSet ? ` (${product.unitsPerSet} Pcs/Set)` : ''}`
    : `MOQ: ${product.moq || 1} Pcs`;
  
  // Calculate availability (check Mongoose isAvailable boolean)
  const isAvailable = product.isAvailable === true;

  // Normalize image data for the cart reducer
  const imageUrls = product.images?.map((img) => 
    typeof img === 'string' ? img : img?.url
  ).filter(Boolean) || [];
  
  const primaryImage = imageUrls[0] || product.imageUrl || '/placeholder_product.png';

  // Standard cart dispatch helper (respecting minimum order quantity)
  const handleAddToCart = async () => {
    if (!isAvailable) {
      alert('Product is currently unavailable.');
      return;
    }

    const isAlreadyInCart = cartItems.some((item) => item.productId === product._id);
    if (isAlreadyInCart) {
      alert('Product already in your cart!');
      return;
    }

    const isSet = product.moqType === 'set';
    const initialQty = isSet
      ? (product.moq || 1) * (product.unitsPerSet || 1)
      : (product.moq || 1);

    if (user && user.token) {
      try {
        const res = await apiFetch('/api/cart', {
          method: 'POST',
          body: JSON.stringify({ productId: product._id, qty: initialQty })
        });
        const data = await res.json();
        if (!res.ok) {
          alert(data.message || 'Failed to add product to cart');
          return;
        }
        dispatch(setCartItems(data));
        alert('Successfully added to your cart!');
      } catch (err) {
        console.error(err);
        alert('Failed to add product to cart');
      }
    } else {
      dispatch(
        addToCart({
          productId: product._id,
          name: product.name,
          price: product.price,
          image: primaryImage,
          imageUrl: primaryImage,
          images: imageUrls,
          qty: initialQty,
          section: product.section,
          category: categoryName,
          moqType: product.moqType || 'pcs',
          unitsPerSet: product.unitsPerSet || 1,
          isAvailable: product.isAvailable
        })
      );
      alert('Successfully added to your cart!');
    }
  };

  // WhatsApp Share trigger
  const handleWhatsAppShare = () => {
    const shareText = `Check out this wholesale product on REGARD TRADERS: ${product.name} - ${window.location.href}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
  };

  // Copy Link to clipboard trigger
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const infoStyle = {
    '--section-color': sectionColor,
    '--section-color-rgb': sectionColor.startsWith('#') 
      ? `${sectionColor.startsWith('#') && sectionColor.length === 7 ? 
          `${parseInt(sectionColor.slice(1,3), 16)}, ${parseInt(sectionColor.slice(3,5), 16)}, ${parseInt(sectionColor.slice(5,7), 16)}` 
          : '255, 106, 0'}`
      : '255, 106, 0',
  };

  return (
    <div className="info-content-panel" style={infoStyle}>
      {/* 1. Category Label */}
      <span className="product-category-label">{categoryName}</span>
      
      {/* 2. Product Name */}
      <h1 className="product-name-title">{product.name}</h1>

      {/* 3. Availability Badge */}
      <div className="availability-container">
        <span className={`info-badge ${isAvailable ? '' : 'badge-disabled'}`}>
          <span className={`badge-status-dot ${isAvailable ? 'in-stock' : 'out-of-stock'}`} />
          {isAvailable ? 'Available' : 'Currently Unavailable'}
        </span>
      </div>

      {/* 4. Price & 5. MOQ Tag */}
      <div className="price-moq-block">
        <h2 className="product-price-tag" style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          <span>₹{product.price.toLocaleString('en-IN')}<span className="product-price-moq-unit"> /pc</span></span>
          {product.mrp && Number(product.mrp) > 0 && (
            <span style={{ fontSize: '1.1rem', color: '#9ca3af', textDecoration: 'line-through', fontWeight: 'normal' }}>
              ₹{Number(product.mrp).toLocaleString('en-IN')}
            </span>
          )}
        </h2>
        <span className="product-moq-tag">{formattedMoq}</span>
      </div>

      {/* 6. Desktop Action Buttons Group */}
      <div className="cta-button-group">
        <button
          onClick={handleAddToCart}
          className="btn-buy-now-desktop"
          disabled={!isAvailable}
        >
          Add To Cart
        </button>
      </div>

      {/* 9. Product Specifications/Attributes List */}
      <ProductSpecs product={product} />

      {/* 7. Product Description (Short Description below specs) */}
      <div className="product-description-container">
        <h4>Product Description</h4>
        <p>{product.description}</p>
      </div>

      {/* 10. Inline Sharing Buttons (No modal layout) */}
      <div className="share-buttons-container">
        <span className="share-title">Share Product</span>
        <button onClick={handleWhatsAppShare} className="share-action-btn" aria-label="Share on WhatsApp">
          <Share2 size={16} />
          <span>WhatsApp</span>
        </button>
        <button onClick={handleCopyLink} className="share-action-btn" aria-label="Copy Product Link">
          <Link2 size={16} />
          <span>Copy Link</span>
        </button>
        {copied && <span className="copied-tooltip">Copied!</span>}
      </div>

      {/* Sticky Mobile CTA Bar: [ Add To Cart ] */}
      <div className="product-mobile-sticky-cta">
        <button
          onClick={handleAddToCart}
          className="btn-sticky-whatsapp"
          disabled={!isAvailable}
          style={{ flex: 1 }}
        >
          <ShoppingCart size={18} />
          Add To Cart
        </button>
      </div>
    </div>
  );
};

export default ProductInfo;
